import { formatDate } from "../../../../common-utilities/utils";
import { DATE_FORMATS } from "../../../../../utils/constants";
import { getAdvanceAmount, getPOAmountWithoutTax } from "../helper";
import { stripEmptyChargeRows } from "../common-components/extra-charges-section";

const getId = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "object") {
    return value.id ?? value._id ?? value.value ?? "";
  }

  return value;
};

export const createPayloadMapper = (
  formDetails,
  items,
  budgetDetails,
  poType,
  documentLinks,
  isPRLinked,
  extraCharges = []
) => {
  const {
    vendor = {},
    department = {},
    currency = "",
    deliveryLocation = {},
    billingLocation = {},
    company = {},
  } = formDetails;

  // PR-linked POs must target the exact store the user picked as Delivery Location --
  // budgetDetails.site is only ever an arbitrary pooled STORE's budget for HEAD_OFFICE
  // PRs, never the store the PO should actually deliver to. Independent (non-PR) POs
  // keep deriving site from the linked budget's own site, unchanged.
  const site = isPRLinked ? getId(deliveryLocation) : budgetDetails?.site || "";
  let orderItems = [];

  Object.keys(items || {}).map((key) => {
    items[key].forEach((item) => {
      const { rate, quantity, taxPercentage = 0 } = item;
      if (!(Number(quantity) > 0)) {
        return;
      }
      let taxAmount = Math.round(
        rate * quantity * (taxPercentage / 100)
      ).toFixed(2);

      const orderItem = {
        lineItem: key,
        item: item?.itemId,
        // Category-mapping selector: category comes from the LineItem context this row was
        // created/edited under, subCategory from the dependent dropdown the user picked --
        // the backend validates this pair against the item's actual categoryMappings at PO
        // creation. On update it isn't re-validated (an item's category is locked once a PO
        // is raised against it), so it's critical this is sent as-is rather than omitted --
        // omitting it would unset category/subCategory on every saved item.
        category: item.category?.id ?? item.category?._id ?? item.category ?? undefined,
        subCategory: item.subCategory?.id ?? item.subCategory?._id ?? item.subCategory ?? undefined,
        uom: item?.uom,
        quantity: item?.quantity,
        rate: item.rate,
        amount: Math.round(rate * quantity).toFixed(2),
        taxRate: item.taxPercentage || 0,
        taxAmount: taxAmount,
        totalAmount: rate * quantity + +taxAmount,
        description: item.description,
        remarks: item.remarks,
        hsnCode: item.hsnCode || "",
      };

      if (item.prItemId) {
        orderItem.prItemId = item.prItemId;
      }

      if (item.ticketNumber) {
        orderItem.ticketNumber = item.ticketNumber;
      }
      if (item.ticketCreationDate) {
        orderItem.ticketCreationDate = item.ticketCreationDate;
      }

      orderItems.push(orderItem);
    });
  });

  let payload = {
    company: company?.id,
    department: department?._id,
    site: site,
    vendorCode: vendor?.code,
    vendorName: vendor?.name,
    vendorPancard: vendor?.pan || "Not found",
    vendorGst: vendor?.gst || "Not found",
    poType: poType,
    currency: currency?.value,
    budget: budgetDetails?.id,
    poDate: formDetails?.poDate,
    remarks: formDetails?.remarks,
    termsAndConditions: formDetails?.termsAndConditions,
    paymentTerms: formDetails?.paymentTerms,
    agreement: formDetails?.agreement,
    advance: {
      advancePercentage: formDetails.advancePercent || 0,
      advanceAmount: getAdvanceAmount(
        formDetails.advancePercent || 0,
        getPOAmountWithoutTax(items)
      ),
    },
    deliveryLocationId: deliveryLocation?.id,
    billingLocationId: billingLocation?.id,
    forex: formDetails.forex,
    items: orderItems,
    // inwardedQuantity/allowInward are server-owned ledger fields the PO GET response now includes -
    // never echo them back, the backend always recomputes them from its own stored state.
    extraCharges: stripEmptyChargeRows(extraCharges).map(
      ({ inwardedQuantity, allowInward, ...charge }) => charge
    ),
  };

  if (documentLinks?.length > 0) {
    payload.documentLinks = documentLinks.map((d) => d.docUrl);
  }

  return payload;
};

export const mapPoFileUploadResponse = (uploadedItems) => {
  return uploadedItems.reduce((group, uploadedItem) => {
    const { lineItemId } = uploadedItem;
    uploadedItem.itemId = uploadedItem.id;
    group[lineItemId] = group[lineItemId] ?? [];
    group[lineItemId].push(uploadedItem);
    return group;
  }, {});
};

export const mapPOResponseToFormDetails = (
  response,
  departmentList,
  vendors
) => {
  const {
    budget = {},
    advance = {},
    company = {},
    poType = "",
    vendorCode = "",
    department,
    currency = {},
    poDate = "",
    items = [],
    poNumber = "",
    termsAndConditions = "",
    creatorName = "",
    approvalId = {},
  } = response;

  const selectedVendor = vendors.find((v) => v.code === vendorCode) || {};

  const poItems = items.reduce((group, itemObject) => {
    const { lineItem = {}, item = {} } = itemObject;

    const { id } = lineItem;
    // Prefer the PO item's own persisted subCategory (stamped when the item was added,
    // resolved against the item's actual categoryMappings) -- item.subCategory is only
    // the Item Master's deprecated single mirror, wrong for a multi-mapping item. Only
    // fall back to it for legacy rows that predate this per-item stamp.
    const subCategory =
      itemObject.subCategory && Object.keys(itemObject.subCategory).length
        ? itemObject.subCategory
        : item?.subCategory || {};
    itemObject.itemId = item?.id;
    itemObject.lineItemId = id;
    itemObject.itemCode = item?.itemCode;
    itemObject.taxPercentage = itemObject?.hasOwnProperty("taxRate")
      ? itemObject?.taxRate
      : item?.taxPercentage;
    // itemObject.hsnCode = item.hsnCode || ''
    itemObject.name = item?.name;
    itemObject.subcategoryName = subCategory?.name;
    itemObject.subcategoryId = subCategory?.id;
    itemObject.subCategory = subCategory;
    group[id] = group[id] ?? [];
    group[id].push(itemObject);
    return group;
  }, {});

  return {
    mappedResponse: {
      department: departmentList.find((d) => d._id === department) || {},
      vendor: selectedVendor,
      currency: {
        value: currency?.id,
        label: currency?.name,
      },
      poDate: formatDate(poDate, DATE_FORMATS["YYYY-MM-DD"]),
      agreement: response?.agreement || "",
      remarks: response?.remarks || "",
      deliveryLocation: response?.deliveryLocationId || {},
      billingLocation: response?.billingLocationId || {},
      company: company,
      paymentTerms: response?.paymentTerms || "",
      advancePercent: advance?.advancePercentage,
      poType,
      budgetDetails: budget,
      forex: response?.forex || 0,
      selectedLineItemIds: items?.map?.(
        (i) => (i.lineItem && i.lineItem.id) || 0
      ),
      poNumber,
      termsAndConditions,
      creatorName,
      approvalId,
    },
    mappedAutoCompleteInputValues: {
      vendor: selectedVendor?.name,
      company: company?.name,
      deliveryLocation: (response.deliveryLocationId || {}).name,
      billingLocation: (response.billingLocationId || {}).name,
      currency: currency?.name,
    },
    poItems,
  };
};
